import React, { useState, useEffect } from 'react';
import { X, ChevronDown, ChevronUp, Info } from 'lucide-react';
import { designTokens } from '../styles/design-tokens';
import { useTheme } from '../context/ThemeContext';
import { Section, Field } from './education/Section';
import api from '../lib/api';

export interface QueueCongestionProperties {
  county: string;
  state: string;
  active_projects: number;
  pending_mw: number;
  completed_projects: number;
  completed_mw: number;
}

interface QueueCongestionDetailProps {
  data: QueueCongestionProperties;
  onClose: () => void;
}

interface QueueProject {
  project_name: string;
  interconnecting_entity: string;
  fuel: string;
  technology: string;
  capacity_mw: number;
  status: string;
  queue_date: string | null;
  proposed_completion: string | null;
  transmission_owner: string | null;
  cdr_reporting_zone: string | null;
}

interface DetailData {
  summary: {
    active_projects: number;
    pending_mw: number;
    completed_projects: number;
    completed_mw: number;
    withdrawn_projects: number;
  };
  projects: QueueProject[];
}

const FUEL_COLORS: Record<string, string> = {
  Gas: '#d97706',
  Solar: '#f59e0b',
  Wind: '#0891b2',
  Battery: '#16a34a',
  Nuclear: '#7c3aed',
};

export const QueueCongestionDetail: React.FC<QueueCongestionDetailProps> = ({ data, onClose }) => {
  const { theme } = useTheme();
  const [detail, setDetail] = useState<DetailData | null>(null);
  const [sourceExpanded, setSourceExpanded] = useState(false);
  const d = data;
  const mw = d.pending_mw;
  const severity = mw > 3000 ? '#dc2626' : mw > 1000 ? '#d97706' : '#16a34a';
  const label = mw > 3000 ? 'High Congestion' : mw > 1000 ? 'Moderate Congestion' : 'Low Congestion';

  useEffect(() => {
    api.get('/power/queue-detail', { params: { county: d.county, state: d.state } })
      .then(res => setDetail(res.data))
      .catch(() => {});
  }, [d.county, d.state]);

  // Aggregate by fuel type
  const fuelBreakdown = detail?.projects.reduce((acc, p) => {
    const fuel = p.fuel || 'Other';
    if (!acc[fuel]) acc[fuel] = { count: 0, mw: 0 };
    acc[fuel].count += 1;
    acc[fuel].mw += p.capacity_mw;
    return acc;
  }, {} as Record<string, { count: number; mw: number }>) || {};

  return (
    <div style={{
      width: designTokens.layout.detailPanelWidth, background: theme.background,
      borderLeft: `1px solid ${theme.border}`,
      display: 'flex', flexDirection: 'column', overflow: 'hidden', flexShrink: 0,
    }}>
      {/* Header */}
      <div style={{
        padding: '16px', borderBottom: `1px solid ${theme.border}`,
        display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
      }}>
        <div>
          <h2 style={{ fontSize: '16px', fontWeight: 700, margin: 0, color: theme.text }}>
            {d.county} Co., {d.state}
          </h2>
          <div style={{
            marginTop: '4px', display: 'inline-block',
            padding: '2px 8px', fontSize: '10px', fontWeight: 600,
            background: severity + '20', color: severity,
            borderRadius: '4px',
          }}>
            {d.state === 'TX' ? 'ERCOT' : d.state} Queue: {label}
          </div>
        </div>
        <button onClick={onClose} style={{
          background: 'none', border: 'none', cursor: 'pointer', padding: '2px',
          color: theme.textSecondary,
        }}>
          <X size={18} />
        </button>
      </div>

      {/* Content */}
      <div style={{ flex: 1, overflow: 'auto', padding: '16px' }}>
        {/* Key metrics */}
        <div style={{ marginBottom: '16px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '4px' }}>
            <span style={{ fontSize: '11px', color: theme.textSecondary }}>Pending Capacity</span>
            <span style={{
              fontFamily: designTokens.typography.fontFamily.mono,
              fontSize: '13px', fontWeight: 600, color: severity,
            }}>{mw.toLocaleString()} MW</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '4px' }}>
            <span style={{ fontSize: '11px', color: theme.textSecondary }}>Active Projects</span>
            <span style={{
              fontFamily: designTokens.typography.fontFamily.mono,
              fontSize: '13px', fontWeight: 600, color: theme.text,
            }}>{d.active_projects}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '4px' }}>
            <span style={{ fontSize: '11px', color: theme.textSecondary }}>Completed</span>
            <span style={{
              fontFamily: designTokens.typography.fontFamily.mono,
              fontSize: '13px', fontWeight: 500, color: theme.textSecondary,
            }}>{d.completed_mw.toLocaleString()} MW ({d.completed_projects})</span>
          </div>
          {detail?.summary.withdrawn_projects ? (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
              <span style={{ fontSize: '11px', color: theme.textSecondary }}>Withdrawn</span>
              <span style={{
                fontFamily: designTokens.typography.fontFamily.mono,
                fontSize: '13px', fontWeight: 500, color: theme.textTertiary,
              }}>{detail.summary.withdrawn_projects}</span>
            </div>
          ) : null}
        </div>

        {/* What this means */}
        {mw > 3000 && (
          <div style={{
            padding: '8px 10px', borderRadius: '6px', marginBottom: '16px',
            background: '#dc262610', border: '1px solid #dc262630',
            fontSize: '10px', color: '#dc2626', lineHeight: 1.5,
          }}>
            {mw.toLocaleString()} MW pending signals heavy transmission congestion. New interconnection requests in this county will face extended study timelines and potential upgrade cost allocation.
          </div>
        )}

        {/* Fuel breakdown */}
        {Object.keys(fuelBreakdown).length > 0 && (
          <Section title="Fuel Mix (Active Queue)">
            {Object.entries(fuelBreakdown)
              .sort((a, b) => b[1].mw - a[1].mw)
              .map(([fuel, data]) => (
                <div key={fuel} style={{
                  display: 'flex', justifyContent: 'space-between', padding: '4px 0',
                  borderBottom: `1px solid ${theme.borderLight}`,
                }}>
                  <span style={{ fontSize: '12px', color: FUEL_COLORS[fuel] || theme.textTertiary, fontWeight: 500 }}>
                    {fuel}
                  </span>
                  <span style={{
                    fontSize: '12px', fontWeight: 500, color: theme.text,
                    fontFamily: designTokens.typography.fontFamily.mono,
                  }}>
                    {data.mw.toLocaleString()} MW ({data.count})
                  </span>
                </div>
              ))}
          </Section>
        )}

        {/* Individual projects */}
        {detail && detail.projects.length > 0 && (
          <Section title={`Active Projects (${detail.projects.length})`}>
            {detail.projects.map((p, i) => (
              <div key={i} style={{
                padding: '6px 0',
                borderBottom: `1px solid ${theme.borderLight}`,
              }}>
                <div style={{ fontSize: '11px', fontWeight: 600, color: theme.text }}>
                  {p.project_name}
                </div>
                <div style={{ fontSize: '10px', color: theme.textSecondary, marginTop: '2px' }}>
                  {p.interconnecting_entity}
                </div>
                <div style={{ display: 'flex', gap: '8px', marginTop: '3px', fontSize: '10px' }}>
                  <span style={{ color: FUEL_COLORS[p.fuel] || theme.textTertiary, fontWeight: 600 }}>
                    {p.fuel} {p.capacity_mw.toLocaleString()} MW
                  </span>
                  {p.proposed_completion && (
                    <span style={{ color: theme.textTertiary }}>
                      Target: {new Date(p.proposed_completion).toLocaleDateString('en-US', { year: 'numeric', month: 'short' })}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </Section>
        )}

        {/* Location */}
        <Section title="Location">
          <Field label="County" value={`${d.county} Co.`} />
          <Field label="State" value={d.state} />
        </Section>

        {/* Data Source */}
        <div style={{ marginTop: '12px' }}>
          <button
            onClick={() => setSourceExpanded(!sourceExpanded)}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: '4px',
              padding: '2px 8px', fontSize: '10px', fontWeight: 600,
              background: '#4b556320', color: '#8A9CAA',
              borderRadius: '4px', border: 'none', cursor: 'pointer',
            }}
          >
            <Info size={10} />
            Data Source
            {sourceExpanded ? <ChevronUp size={10} /> : <ChevronDown size={10} />}
          </button>
          {sourceExpanded && (
            <div style={{
              marginTop: '6px', padding: '8px', fontSize: '10px',
              border: `1px solid ${theme.borderLight}`,
              borderRadius: '6px', lineHeight: 1.6,
            }}>
              <div style={{ color: theme.text, fontWeight: 600, marginBottom: '4px' }}>
                ERCOT Generator Interconnection Status (GIS)
              </div>
              <div style={{ color: theme.textTertiary }}>
                Published quarterly by ERCOT. Shows all generation projects in the interconnection queue — pending, completed, and withdrawn.
              </div>
              <div style={{ color: theme.textTertiary, marginTop: '4px' }}>
                High pending MW indicates transmission congestion. New load or generation interconnection requests face longer study timelines and potential network upgrade cost allocation.
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
